// "Browse by switch" — the React port of the PHP search page's Switch
// mode: a searchable grid of switches (each tile shows the configured
// roster size, the live online count behind it, and the uplink-ONU
// online state as the tile-icon colour), and picking one opens a dialog
// listing the subscribers behind it. Lives inside the home page's
// "Switch" tab; stays live via useSwitchList's SSE refresh.

import { useState } from "react";
import { Network, Router, Search } from "lucide-react";
import { Alert, AlertDescription, Card, Input, Skeleton, cn } from "@noc/ui";
import { useDebounced } from "@/lib/useDebounced";
import { useSwitchList } from "./useSwitches";
import { SwitchUsersDialog } from "./SwitchUsersDialog";
import type { SwitchSummary } from "@noc/api";

export function SwitchPicker() {
  const { data, isPending, isError, error } = useSwitchList();
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const q = useDebounced(filter.trim().toLowerCase(), 150);

  const all = data?.ok ? data.switches : [];
  const querying = q.length >= 2;
  const filtered = querying ? all.filter((s) => s.switch_name.toLowerCase().includes(q)) : [];

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search switches…"
            autoComplete="off"
            autoFocus
            className="pl-9"
          />
        </div>
        <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
          {isPending
            ? "…"
            : querying
              ? `${filtered.length} match${filtered.length === 1 ? "" : "es"}`
              : `${all.length} switches`}
        </span>
      </div>

      {isError && (
        <Alert variant="destructive" className="mt-3">
          <AlertDescription>
            {error instanceof Error ? error.message : "Failed to load switches."}
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-3">
        {isPending ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : !querying ? (
          <PickerEmpty
            title="Search switches"
            sub={all.length ? `Type at least 2 characters — ${all.length} switches available` : "No switches found"}
          />
        ) : filtered.length === 0 ? (
          <PickerEmpty title="No switches found" sub={`Nothing matches “${filter.trim()}”`} />
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {filtered.map((s) => (
              <SwitchTile key={s.switch_name} s={s} onClick={() => setSelected(s.switch_name)} />
            ))}
          </div>
        )}
      </div>

      <SwitchUsersDialog switchName={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function PickerEmpty({ title, sub }: { title: string; sub: string }) {
  return (
    <Card className="border-dashed">
      <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
        <span className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Network className="size-6" />
        </span>
        <p className="text-sm font-medium">{title}</p>
        <p className="max-w-sm text-sm text-muted-foreground">{sub}</p>
      </div>
    </Card>
  );
}

function SwitchTile({ s, onClick }: { s: SwitchSummary; onClick: () => void }) {
  // ONU online state of the switch's own uplink box — true / false / null
  // (null = no ONU data, render neutral). The JS mirrors PHP's colouring.
  const tone =
    s.onu_online === true
      ? "bg-success/15 text-success"
      : s.onu_online === false
        ? "bg-destructive/15 text-destructive"
        : "bg-muted text-muted-foreground";
  const Icon = s.onu_online === null ? Network : Router;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 rounded-lg border bg-card p-3 text-left transition-colors hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className={cn("flex size-9 shrink-0 items-center justify-center rounded-md", tone)}>
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{s.switch_name}</span>
        <span className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="inline-block size-1.5 rounded-full bg-success" />
          <span className="text-foreground">{s.online_count}</span> online{" "}
          <span className="opacity-60">/ {s.total}</span>
        </span>
      </span>
    </button>
  );
}
